/**
 * Swarm Dashboard
 * Real-time visualization of swarm execution
 */

import React, { useEffect, useState, useCallback } from 'react';
import { SwarmClient, SwarmSession, type SwarmStatus, type SwarmEvent } from '@mindcore/sdk';
import { 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Clock,
  Users,
  Play,
  Pause,
  Square,
  ArrowLeft,
} from 'lucide-react';

interface SwarmDashboardProps {
  client: SwarmClient;
  swarmId: string;
  onClose: () => void;
}

const PHASE_LABELS: Record<string, { label: string; color: string }> = {
  idle: { label: 'Idle', color: 'bg-gray-500' },
  blueprinting: { label: 'Designing Architecture', color: 'bg-purple-500' },
  swarm_initializing: { label: 'Initializing Agents', color: 'bg-blue-500' },
  parallel_implementation: { label: 'Building', color: 'bg-yellow-500' },
  multi_review: { label: 'Reviewing', color: 'bg-orange-500' },
  consensus_reached: { label: 'Consensus Reached', color: 'bg-green-500' },
  security_validation: { label: 'Security Audit', color: 'bg-red-500' },
  deployment_prep: { label: 'Preparing Deployment', color: 'bg-indigo-500' },
  multi_cloud_deploy: { label: 'Deploying', color: 'bg-cyan-500' },
  completed: { label: 'Completed', color: 'bg-green-600' },
  failed: { label: 'Failed', color: 'bg-red-600' },
};

const AGENT_ICONS: Record<string, string> = {
  blueprint: '🏗️',
  implementer: '⚡',
  reviewer: '🔍',
  security: '🛡️',
  tester: '🧪',
  devops: '🚀',
  fixer: '🔧',
  orchestrator: '🎛️',
};

const AGENT_NAMES: Record<string, string> = {
  blueprint: 'Blueprint Architect',
  implementer: 'Code Implementer',
  reviewer: 'Code Reviewer',
  security: 'Security Auditor',
  tester: 'Test Engineer',
  devops: 'DevOps Engineer',
  fixer: 'Error Fixer',
  orchestrator: 'Orchestrator',
};

export const SwarmDashboard: React.FC<SwarmDashboardProps> = ({
  client,
  swarmId,
  onClose,
}) => {
  const [session, setSession] = useState<SwarmSession | null>(null);
  const [state, setState] = useState<SwarmStatus | null>(null);
  const [events, setEvents] = useState<SwarmEvent[]>([]);
  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let currentSession: SwarmSession | null = null;

    const connect = async () => {
      try {
        setIsConnecting(true);
        const sess = await client.connect(swarmId);
        
        if (!mounted) return;
        
        currentSession = sess;
        setSession(sess);

        // Get initial state
        const initialState = await sess.getStatus();
        setState(initialState);

        // Subscribe to events
        sess.on('*', (event: SwarmEvent) => {
          setEvents(prev => [...prev.slice(-99), event]);
        });

        // Subscribe to state changes
        const pollInterval = setInterval(async () => {
          try {
            const newState = await sess.getStatus();
            setState(newState);
          } catch {
            // Ignore polling errors
          }
        }, 2000);

        setIsConnecting(false);

        return () => {
          clearInterval(pollInterval);
        };
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : 'Failed to connect');
          setIsConnecting(false);
        }
      }
    };

    connect();

    return () => {
      mounted = false;
      currentSession?.close();
    };
  }, [client, swarmId]);

  const handlePause = async () => {
    try {
      await session?.pause();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to pause');
    }
  };

  const handleResume = async () => {
    try {
      await session?.resume();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resume');
    }
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel this swarm?')) return;
    try {
      await session?.cancel();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    }
  };

  if (isConnecting) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Connecting to swarm...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <XCircle className="w-6 h-6 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900">Connection Error</h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={onClose}
            className="text-red-700 underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const phase = PHASE_LABELS[state?.phase || 'idle'] || PHASE_LABELS.idle;
  const isActive = state?.status === 'active';
  const isPaused = state?.status === 'paused';
  const isCompleted = state?.status === 'completed';
  const isFailed = state?.status === 'failed';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Swarm: {swarmId.slice(0, 8)}...</h2>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${phase.color}`}>
                {phase.label}
              </span>
              <span className="text-sm text-gray-500">
                {state?.agents?.size || 0} agents active
              </span>
            </div>
          </div>
        </div>

        {/* Control Buttons */}
        <div className="flex items-center space-x-2">
          {isActive && (
            <button
              onClick={handlePause}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200"
            >
              <Pause className="w-4 h-4" />
              <span>Pause</span>
            </button>
          )}
          {isPaused && (
            <button
              onClick={handleResume}
              className="flex items-center space-x-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
            >
              <Play className="w-4 h-4" />
              <span>Resume</span>
            </button>
          )}
          {!isCompleted && !isFailed && (
            <button
              onClick={handleCancel}
              className="flex items-center space-x-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              <Square className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-500">
            {Math.round(calculateProgress(state))}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${calculateProgress(state)}%` }}
          />
        </div>
      </div>

      {/* Agent Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from(state?.agents?.entries() || []).map(([id, agent]: [string, any]) => (
          <AgentCard
            key={id}
            agentId={id}
            agent={agent}
          />
        ))}
      </div>

      {/* Consensus Panel */}
      {state?.consensus && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2" />
            Consensus Status
          </h3>
          <div className="flex items-center space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {state.consensus.approvedBy.length}
              </div>
              <div className="text-sm text-gray-500">Approvals</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {state.consensus.rejectedBy.length}
              </div>
              <div className="text-sm text-gray-500">Rejections</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">
                {state.consensus.requiredApprovals}
              </div>
              <div className="text-sm text-gray-500">Required</div>
            </div>
            {state.consensus.blockingIssues.length > 0 && (
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {state.consensus.blockingIssues.length}
                </div>
                <div className="text-sm text-gray-500">Blocking Issues</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Event Log */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Log</h3>
        <div className="h-64 overflow-y-auto space-y-2 font-mono text-sm">
          {events.slice(-20).map((event, i) => (
            <div key={i} className="flex items-start space-x-2 p-2 hover:bg-gray-50 rounded">
              <span className="text-gray-400 text-xs">
                {new Date(event.timestamp).toLocaleTimeString()}
              </span>
              <span className="text-blue-600">{event.type}</span>
              <span className="text-gray-600 truncate">
                {JSON.stringify(event.payload).slice(0, 100)}
              </span>
            </div>
          ))}
          {events.length === 0 && (
            <div className="text-gray-400 italic">No events yet...</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Agent Card Component
const AgentCard: React.FC<{ agentId: string; agent: any }> = ({ agentId, agent }) => {
  const icon = AGENT_ICONS[agent.agentId] || '🤖';
  const name = AGENT_NAMES[agent.agentId] || agent.agentId;

  const getStatusIcon = () => {
    switch (agent.state) {
      case 'working':
        return <Loader2 className="w-5 h-5 animate-spin text-blue-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'idle':
        return <Clock className="w-5 h-5 text-gray-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{icon}</span>
          <div>
            <h4 className="font-medium text-gray-900">{name}</h4>
            <p className="text-xs text-gray-500 capitalize">{agent.state}</p>
          </div>
        </div>
        {getStatusIcon()}
      </div>

      {/* Progress */}
      {agent.progressPercent > 0 && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{Math.round(agent.progressPercent)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all"
              style={{ width: `${agent.progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {/* Current Task */}
      {agent.currentTask && (
        <div className="mt-3 text-sm">
          <span className="text-gray-500">Current: </span>
          <span className="text-gray-700 truncate">{agent.currentTask}</span>
        </div>
      )}
    </div>
  );
};

// Calculate progress based on phase
function calculateProgress(state: SwarmStatus | null): number {
  if (!state) return 0;
  
  const phaseProgress: Record<string, number> = {
    idle: 0,
    blueprinting: 10,
    swarm_initializing: 20,
    parallel_implementation: 40,
    multi_review: 60,
    consensus_reached: 70,
    security_validation: 80,
    deployment_prep: 90,
    multi_cloud_deploy: 95,
    completed: 100,
    failed: 0,
  };

  return phaseProgress[state.phase] || 0;
}
